import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaArrowRight, FaClock, FaLock } from 'react-icons/fa';
import { getImageUrl } from '../api';

/**
 * Renders the apply button with a countdown if the position hasn't opened yet,
 * or a closed button if the deadline has passed.
 */
const ApplyButton = ({ formUrl, openDate, deadline }) => {
  const [status, setStatus] = useState('loading'); // 'loading' | 'coming_soon' | 'open' | 'closed'

  useEffect(() => {
    const checkStatus = () => {
      const now = new Date();
      const openTime = openDate ? new Date(openDate) : new Date(0);
      const closeTime = deadline ? new Date(deadline) : new Date(8640000000000000); // Max date

      if (now > closeTime) {
        setStatus('closed');
      } else if (now < openTime) {
        setStatus('coming_soon');
      } else {
        setStatus('open');
      }
    };

    checkStatus();
    const timer = setInterval(checkStatus, 1000);
    return () => clearInterval(timer);
  }, [openDate, deadline]);

  if (status === 'loading') return null;

  if (status === 'closed') {
    return (
      <div className="w-full text-center py-3 px-5 rounded-xl bg-white/5 text-gray-500 text-sm font-bold flex items-center justify-center gap-2 cursor-not-allowed border border-white/5">
        <FaLock className="text-xs" /> Applications Closed
      </div>
    );
  }

  if (status === 'coming_soon') {
    return (
      <div className="w-full text-center py-2 px-5 rounded-xl bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20 flex flex-col items-center justify-center gap-0.5 cursor-not-allowed">
        <div className="flex items-center gap-2 text-sm font-bold">
          <FaClock /> Coming Soon
        </div>
      </div>
    );
  }

  return (
    <a
      href={formUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="w-full text-center py-3 px-5 rounded-xl bg-white/5 text-white text-sm font-bold flex items-center justify-center gap-2 group-hover:bg-[#D4AF37] group-hover:text-black transition-all duration-300 border border-white/10"
    >
      Apply for this Role <FaArrowRight className="text-xs" />
    </a>
  );
};

/**
 * A single position card in the board positions grid.
 *
 * @param {object}  position  - Position data object from data/positions.js
 * @param {number}  index     - Card index (used for animation stagger)
 * @param {string}  deadline  - Dynamic deadline date string
 */
const PositionCard = ({ position, index, deadline }) => {
  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.08 }}
      whileHover={{ y: -5 }}
      className="group flex flex-col bg-white/5 border border-white/10 rounded-[2rem] shadow-none hover:shadow-2xl hover:shadow-[#D4AF37]/10 transition-all duration-300 overflow-hidden backdrop-blur-sm"
    >
      {/* ── Banner Image ── */}
      <div className="relative w-full h-44 overflow-hidden bg-black/20">
        <img
          src={getImageUrl(position.image)}
          alt={`${position.title} position banner`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
      </div>

      {/* ── Card Body ── */}
      <div className="flex flex-col flex-1 px-6 py-5">
        {/* Title */}
        <h3 className="text-xl font-bold text-white mb-3 leading-tight">
          {position.title}
        </h3>
        
        {/* Description */}
        <p className="text-gray-400 text-sm leading-relaxed mb-5 flex-1">
          {position.description}
        </p>

        {/* Apply Button / Countdown */}
        <ApplyButton formUrl={position.formUrl} openDate={position.openDate} deadline={deadline} />
      </div>
    </motion.article>
  );
};

export default PositionCard;
